///
/// EarthBrowser
///
/// Created by Matt Giger on 4/9/2024
/// Copyright (c) 2024 Geodata Labs
///

import { requestRender } from "@stovokor/engine/RenderRequest"
import { matrix } from "@stovokor/math/matrix"
import { type vec3 } from "@stovokor/math/vector"

/**
 * Represents a simple transform in 3D space with it's inverse.
 */
export class Transform {
	protected _transform: matrix = matrix.identity
	protected _inverse?: matrix
	protected _nonScaling?: matrix

	/**
	 * Gets the position of the transform.
	 * @returns The position of the transform.
	 */
	public get position(): vec3 {
		return this._transform.position
	}

	/**
	 * Translates the transform by the given x, y, and z values.
	 * @param x - The x-coordinate to translate by.
	 * @param y - The y-coordinate to translate by.
	 * @param z - The z-coordinate to translate by.
	 */
	public translate(x: number, y: number, z: number): void {
		const current = this._transform.glArrayAccess()
		if(current[12] === x && current[13] === y && current[14] === z) {
			return
		}
		const xform = this._transform.arrayCopy()
		xform[12] = x
		xform[13] = y
		xform[14] = z
		this._transform = new matrix(xform)
		this._inverse = undefined
		this._nonScaling = undefined
		requestRender()
	}

	/**
	 * Scales the transform by the given x, y, and z values.
	 * @param x - The x-coordinate to scale by.
	 * @param y - The y-coordinate to scale by.
	 * @param z - The z-coordinate to scale by.
	 */
	public scale(x: number, y: number, z: number): void {
		const current = this._transform.glArrayAccess()
		if(current[0] === x && current[5] === y && current[10] === z) {
			return
		}
		const xform = this._transform.arrayCopy()
		xform[0] = x
		xform[5] = y
		xform[10] = z
		this._transform = new matrix(xform)
		this._inverse = undefined
		this._nonScaling = undefined
		requestRender()
	}

	/**
	 * Gets the transform matrix.
	 * @returns The transform matrix.
	 */
	public get transform(): matrix {
		return this._transform
	}

	/**
	 * Sets the transform matrix.
	 * @param value - The transform matrix to set.
	 */
	public set transform(value: matrix) {
		// Many nodes recompute and assign their transform every frame; only
		// an actual change should wake the frame loop
		const changed = value !== this._transform && !value.equals(this._transform)
		this._transform = value
		if(changed) {
			this._inverse = undefined
			this._nonScaling = undefined
			requestRender()
		}
	}

	/**
	 * Gets the non-scaling transform matrix. Cached until the transform
	 * changes so callers that push it every frame get a stable object
	 * @returns The non-scaling transform matrix.
	 */
	public get nonScalingTransform(): matrix {
		if(this._nonScaling === undefined) {
			const out = this._transform.arrayCopy()
			out[0] = 1
			out[5] = 1
			out[10] = 1
			this._nonScaling = new matrix(out)
		}
		return this._nonScaling
	}

	/**
	 * Gets the inverse transform matrix.
	 * @returns The inverse transform matrix.
	 */
	public get inverse(): matrix {
		this._inverse ??= this._transform.invert()
		return this._inverse
	}
}